(() => {
  const MODES = {
    '49819528397092': 'general',
    '49819528462628': 'express',
    '49849842794788': 'sameDay',
    '55809532068132': 'specified',
  };

  const TAIWAN_PUBLIC_HOLIDAYS = new Set([
    '2026-01-01', '2026-02-16', '2026-02-17', '2026-02-18', '2026-02-19',
    '2026-02-20', '2026-02-27', '2026-04-03', '2026-04-06', '2026-05-01',
    '2026-06-19', '2026-09-25', '2026-09-28', '2026-10-09', '2026-10-26',
    '2026-12-25',
    '2027-01-01', '2027-02-04', '2027-02-05', '2027-02-08', '2027-02-09',
    '2027-02-10', '2027-03-01', '2027-04-05', '2027-04-06', '2027-04-30',
    '2027-06-09', '2027-09-15', '2027-09-28', '2027-10-11', '2027-10-25',
    '2027-12-24', '2027-12-31',
  ]);

  const root = () => window.Shopify?.routes?.root || '/';

  const formatLocalDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const parseLocalDate = (value) => {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    date.setHours(0, 0, 0, 0);
    return formatLocalDate(date) === value ? date : null;
  };

  const taipeiToday = () => {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: 'Asia/Taipei',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(new Date());
    const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
    const date = new Date(Number(values.year), Number(values.month) - 1, Number(values.day));
    date.setHours(0, 0, 0, 0);
    return date;
  };

  const isTaiwanPublicHoliday = (date) => TAIWAN_PUBLIC_HOLIDAYS.has(formatLocalDate(date));

  const isWorkingDay = (date) => {
    const day = date.getDay();
    return day !== 0 && day !== 6 && !isTaiwanPublicHoliday(date);
  };

  const isDeliveryDay = (date) => date.getDay() !== 0;

  const nextWorkingDay = (startDate) => {
    const date = new Date(startDate);
    date.setHours(0, 0, 0, 0);
    do {
      date.setDate(date.getDate() + 1);
    } while (!isWorkingDay(date));
    return date;
  };

  const addWorkingDays = (startDate, count) => {
    const date = new Date(startDate);
    date.setHours(0, 0, 0, 0);
    let added = 0;
    while (added < count) {
      date.setDate(date.getDate() + 1);
      if (isWorkingDay(date)) added += 1;
    }
    return date;
  };

  const addMonthsClamped = (date, months) => {
    const result = new Date(date);
    const originalDay = result.getDate();
    result.setDate(1);
    result.setMonth(result.getMonth() + months);
    const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
    result.setDate(Math.min(originalDay, lastDay));
    return result;
  };

  const sameDayExpected = (today) => {
    const date = new Date(today);
    date.setHours(0, 0, 0, 0);
    const weekday = date.getDay();
    if (weekday === 5 || weekday === 6 || weekday === 0 || !isWorkingDay(date)) {
      return nextWorkingDay(nextWorkingDay(date));
    }
    return nextWorkingDay(date);
  };

  const getModeConfiguration = (mode, today) => {
    let minDate;
    let maxDate;

    if (mode === 'general') {
      minDate = addWorkingDays(today, 4);
      maxDate = addWorkingDays(today, 7);
    } else if (mode === 'express') {
      minDate = addWorkingDays(today, 2);
      maxDate = addWorkingDays(today, 3);
    } else if (mode === 'sameDay') {
      minDate = sameDayExpected(today);
      maxDate = new Date(minDate);
    } else if (mode === 'specified') {
      minDate = addWorkingDays(today, 8);
      maxDate = addMonthsClamped(minDate, 3);
    }

    return { minDate, maxDate };
  };

  const isSelectableDeliveryDate = (date, minDate, maxDate, mode) => {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return false;
    if (!isDeliveryDay(date)) return false;
    if (minDate && date < minDate) return false;
    if (maxDate && date > maxDate) return false;
    if (mode === 'sameDay' && minDate && formatLocalDate(date) !== formatLocalDate(minDate)) return false;
    return true;
  };

  const getCart = async () => {
    const response = await fetch(`${root()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error('cart-read-failed');
    return response.json();
  };

  const changeLine = async (payload) => {
    const response = await fetch(`${root()}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw new Error('cart-change-failed');
    return response.json();
  };

  const validateCart = async () => {
    const cart = await getCart();
    const today = taipeiToday();

    for (const item of cart.items || []) {
      const mode = MODES[String(item.variant_id)];
      if (!mode) continue;

      const configuration = getModeConfiguration(mode, today);
      const properties = item.properties || {};
      const dateValue = String(properties['指定收件日'] || '');

      if (mode === 'sameDay') {
        const expectedDate = formatLocalDate(configuration.minDate);
        if (dateValue !== expectedDate) {
          await changeLine({
            id: item.key,
            quantity: item.quantity,
            properties: { ...properties, '指定收件日': expectedDate },
          });
        }
        continue;
      }

      const selected = parseLocalDate(dateValue);
      if (!isSelectableDeliveryDate(selected, configuration.minDate, configuration.maxDate, mode)) {
        return { ok: false, item };
      }
    }

    return { ok: true };
  };

  const redirectForReselection = async (item) => {
    const time = String(item.properties?.['收件時段'] || '');
    await changeLine({ id: item.key, quantity: 0 });

    const params = new URLSearchParams({
      variant: String(item.variant_id),
      mustflor_delivery_expired: '1',
    });
    if (time) params.set('mustflor_time', time);
    window.location.assign(`${root()}products/sr001?${params.toString()}`);
  };

  let bypass = false;
  let busy = false;

  document.addEventListener('submit', (event) => {
    const submitter = event.submitter;
    if (bypass || !submitter || submitter.name !== 'checkout') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;

    busy = true;
    submitter.disabled = true;

    validateCart()
      .then(async (result) => {
        if (!result.ok) {
          await redirectForReselection(result.item);
          return;
        }

        bypass = true;
        if (typeof event.target.requestSubmit === 'function') {
          event.target.requestSubmit(submitter);
        } else {
          window.location.assign(`${root()}checkout`);
        }
      })
      .catch(() => {
        window.alert('目前無法確認指定收件日期，請重新整理頁面後再試一次。');
      })
      .finally(() => {
        busy = false;
        if (!bypass && document.contains(submitter)) submitter.disabled = false;
      });
  }, true);
})();